import React, { useEffect, useState } from 'react';
import { Layout, Button, Dropdown, Avatar, message } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import ConversationList from './components/ConversationList';
import ChatArea from './components/ChatArea';
import InputArea from './components/InputArea';
import ImageLinks from './components/ImageLinks';
import QuickQuestions from './components/QuickQuestions';
import ThemeSelector from '../../components/ThemeSelector';
import LoginModal from '../../components/LoginModal';
import { getPublicSettings, getPublicQuickLinks, type SystemSettings, type QuickLink } from '../../api/public';
import { getMediaUrl } from '../../utils/request';
import './index.css';

const { Header, Content } = Layout;

const Home: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme,
    sidebarLeftCollapsed,
    toggleSidebarLeft,
    conversations,
    currentConversationId,
    loadConversations,
    setCurrentConversation,
  } = useChatStore();
  const { logout, isAuthenticated, user } = useAuthStore();
  const { setTheme } = useThemeStore();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [quickLinks, setQuickLinks] = useState<QuickLink[]>([]);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 获取公共设置和快捷链接
  useEffect(() => {
    const fetchPublicData = async () => {
      try {
        const [settingsRes, linksRes] = await Promise.all([
          getPublicSettings(),
          getPublicQuickLinks(),
        ]);
        if (settingsRes.code === 200) {
          setSystemSettings(settingsRes.data);
          if (settingsRes.data.site_name) {
            document.title = settingsRes.data.site_name;
          }
        }
        if (linksRes.code === 200) setQuickLinks(linksRes.data);
      } catch (error) {
        console.error('Failed to fetch public data:', error);
      }
    };
    fetchPublicData();
  }, []);

  // 暂时隐藏：主题图标按钮（ThemeSelector）
  const showThemeSelector = false;

  // 初始化主题
  useEffect(() => {
    setTheme('orange'); // 默认橙色主题
  }, [setTheme]);

  // 登录用户加载对话列表
  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
    }
  }, [isAuthenticated, loadConversations]);

  // 监听快捷提问事件
  useEffect(() => {
    const handleQuickQuestion = (event: CustomEvent) => {
      const { question } = event.detail;
      // 触发输入框设置值
      const inputEvent = new CustomEvent('setInputValue', { detail: { value: question } });
      window.dispatchEvent(inputEvent);
    };

    window.addEventListener('quickQuestion', handleQuickQuestion as EventListener);
    return () => {
      window.removeEventListener('quickQuestion', handleQuickQuestion as EventListener);
    };
  }, []);

  // 应用主题
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'profile') {
      navigate('/profile');
    } else if (key === 'admin') {
      window.open('/admin', '_blank');
    } else if (key === 'logout') {
      logout();
      message.success('已登出');
    }
  };

  const isAdmin = user?.role === 'admin';
  const menuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
    },
    ...(isAdmin
      ? [
          {
            key: 'admin',
            icon: <DashboardOutlined />,
            label: '管理后台',
          },
        ]
      : []),
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleLogoClick = () => {
    setCurrentConversation(null);
    // 回到顶部
    const wrapper = document.querySelector('.chat-content-wrapper');
    if (wrapper) wrapper.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout className={`home-layout ${theme === 'dark' ? 'dark' : ''} ${isMobile ? 'mobile-view' : ''}`}>
      <Header className="home-header">
        <div className="header-left">
          <Button
            type="text"
            icon={sidebarLeftCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={toggleSidebarLeft}
            className="sidebar-toggle-btn"
          />
          <div 
            className="header-title" 
            onClick={handleLogoClick}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <img
              src={systemSettings?.logo_url ? getMediaUrl(systemSettings.logo_url) : `${process.env.PUBLIC_URL}/mascot/micah-logo.png`}
              alt="logo"
              style={{ height: '32px', width: 'auto' }}
            />
            {systemSettings?.site_name || '米迦同学-装修AI助手'}
          </div>
        </div>
        <div className="header-right">
          {showThemeSelector && <ThemeSelector />}
          {isAuthenticated && user && (
            <div className="user-info" style={{ marginRight: '12px', fontSize: '14px' }}>
              {user.nickname || user.username}
            </div>
          )}
          {isAuthenticated ? (
            <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} placement="bottomRight">
              <Avatar
                className="header-avatar header-avatar--logged-in"
                size={44}
                src={user?.avatar}
                icon={<UserOutlined />}
              />
            </Dropdown>
          ) : (
            <Avatar
              className="header-avatar header-avatar--guest"
              size={44}
              icon={<UserOutlined />}
              onClick={() => setLoginModalOpen(true)}
            />
          )}
        </div>
      </Header>
      <Content className="home-content">
        <div className="chat-layout">
          <ConversationList
            collapsed={sidebarLeftCollapsed}
            onToggle={toggleSidebarLeft}
            systemSettings={systemSettings}
            quickLinks={quickLinks}
          />
          <div className="chat-main"
                 style={{ 
                   marginLeft: isMobile ? '0' : (sidebarLeftCollapsed ? '0' : '280px')
                 }}
            >
              <div className="chat-content-wrapper">
                {(() => {
                  // 检查当前对话是否有消息
                  const currentConversation = conversations.find(
                    (conv) => conv.id === currentConversationId
                  );
                  const hasMessages = currentConversation?.messages && currentConversation.messages.length > 0;
                  
                  if (hasMessages) {
                    return (
                      <div className="chat-area-wrapper">
                        <ChatArea theme={theme} systemSettings={systemSettings} />
                      </div>
                    );
                  }

                  // 只在没有消息时显示图片链接和快捷提问
                  // 如果没有当前对话，也显示（新问题状态）
                  return (
                    <div className="home-welcome-area">
                      <div className="chat-area-wrapper">
                        <ChatArea theme={theme} systemSettings={systemSettings} />
                      </div>
                      {!isMobile && (
                        <ImageLinks 
                          links={quickLinks.length > 0 ? quickLinks.map(l => ({
                            id: String(l.id),
                            title: l.title,
                            image: getMediaUrl(l.image_url),
                            url: l.link_url,
                            description: l.description,
                          })) : undefined} 
                        />
                      )}
                      <QuickQuestions />
                    </div>
                  );
                })()}
              </div>
              <div className="input-area-wrapper">
                <InputArea onRequestLogin={() => setLoginModalOpen(true)} />
              </div>
            </div>
        </div>
      </Content>
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </Layout>
  );
};

export default Home;
